import { colors, space } from '@ardoq/design-tokens';
import {
  DropdownContainer,
  DropdownFilterInput,
  DropdownMaxHeight,
  DropdownMaxWidth,
  DropdownMenu,
  DropdownLabel,
  DropdownOption,
} from '@ardoq/dropdown-2026';
import { Checkbox } from '@ardoq/forms';
import { Icon, IconSize, MinimalIconProps, SpinnerIcon } from '@ardoq/icons';
import { FlexBox, Stack } from '@ardoq/layout';
import { header5, Text } from '@ardoq/typography';
import styled from 'styled-components';
import { DropdownFootnote } from './DropdownFootnote';
import { Separator } from './Separator';
import { Fragment } from 'react';

export type HierarchicalListCheckboxState = 'unchecked' | 'checked' | 'mixed';

type HierarchicalListPopoverOptionBase = {
  id: string;
  label: string;
  icon: MinimalIconProps | null;
  tooltip?: string;
  children?: HierarchicalListPopoverOptionProps[];
  parentId: string | null;
};

export type HierarchicalListPopoverOptionProps =
  | (HierarchicalListPopoverOptionBase & {
      isReadOnly: true;
    })
  | (HierarchicalListPopoverOptionBase & {
      isReadOnly: false;
      checkboxState: HierarchicalListCheckboxState;
      onClick: VoidFunction;
      onCheckboxClick: VoidFunction;
      hideCheckbox: boolean;
      isDisabled: boolean;
    });

type HierarchicalListPopoverGroupProps = {
  variant?: 'flat' | 'hierarchical';
  label?: string;
  options: HierarchicalListPopoverOptionProps[];
};

export type HierarchicalListPopoverProps = {
  type: 'hierarchicalList';
  maxHeight?: DropdownMaxHeight;
  maxWidth?: DropdownMaxWidth;
  filter?: {
    isLoading?: boolean;
    placeholder?: string;
    value: string;
    onChange: (value: string) => void;
  };
  groups: HierarchicalListPopoverGroupProps[];
  loadingLabel?: string;
  noResultsLabel?: string;
  footer?: string;
};

const GroupTitle = styled.h5`
  ${header5};
  padding: ${space.small} ${space.medium};
  margin: 0;
  color: ${colors.textSubtle};
`;

const INDENT_PER_LEVEL = 20;
const IndentWrapper = styled(FlexBox)<{ $depth: number }>`
  padding-left: ${({ $depth }) => $depth * INDENT_PER_LEVEL}px;
`;

// CheckboxWrapper aligns indentation for options without a checkbox
const CheckboxWrapper = styled.div`
  min-width: 28px; /* equal to the width of the checkbox (including padding) */
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

type TreeItemAriaProps = {
  'aria-level': number;
  'aria-setsize': number;
  'aria-posinset': number;
};

const IconAndLabel = ({
  node,
  variant,
  color,
}: {
  node: HierarchicalListPopoverOptionProps;
  variant?: 'bold';
  color?: 'textSubtle';
}) => (
  <FlexBox align="center" flex={1} gap="small">
    {node.icon && (
      <Icon
        iconName={node.icon.name}
        color={node.icon.color ?? colors.icon}
        iconSize={IconSize.SMALL}
      />
    )}
    <Text variant={variant === 'bold' ? 'text2Bold' : 'text2'} color={color}>
      {node.label}
    </Text>
  </FlexBox>
);

const ReadOnlyBranchLabel = ({
  node,
}: {
  node: Extract<HierarchicalListPopoverOptionProps, { isReadOnly: true }>;
}) => (
  <FlexBox
    align="center"
    data-tooltip-text={node.tooltip}
    paddingX="medium"
    paddingY="xsmall"
  >
    <IconAndLabel node={node} color="textSubtle" />
  </FlexBox>
);

type SelectableOption = Extract<
  HierarchicalListPopoverOptionProps,
  { isReadOnly: false }
>;

const CheckboxSlot = ({ node }: { node: SelectableOption }) => (
  <CheckboxWrapper>
    {!node.hideCheckbox && (
      <Checkbox
        name={node.label}
        aria-label={node.label}
        isChecked={node.checkboxState === 'checked'}
        isMixed={node.checkboxState === 'mixed'}
        isDisabled={node.isDisabled}
        onChange={node.onCheckboxClick}
        tabIndex={-1}
        stopPropagation={true}
      />
    )}
  </CheckboxWrapper>
);

const HierarchicalListPopoverOption = ({
  node,
  depth,
  treeItemAria,
}: {
  node: HierarchicalListPopoverOptionProps;
  depth: number;
  treeItemAria: TreeItemAriaProps;
}) => {
  const variant = node.children?.length ? 'bold' : undefined;

  if (node.isReadOnly) {
    return (
      <div role="treeitem" aria-label={node.label} {...treeItemAria}>
        <DropdownLabel data-tooltip-text={node.tooltip}>
          <IndentWrapper $depth={depth} align="center">
            {depth > 0 && <CheckboxWrapper />}
            <IconAndLabel node={node} variant={variant} />
          </IndentWrapper>
        </DropdownLabel>
      </div>
    );
  }

  return (
    <DropdownOption
      data-tooltip-text={node.tooltip}
      aria-label={node.label}
      role="treeitem"
      isDisabled={node.isDisabled}
      onClick={node.onClick}
      onKeyDown={e => {
        if (e.key === ' ' && !node.hideCheckbox && !node.isDisabled) {
          e.preventDefault();
          node.onCheckboxClick();
        }
      }}
      {...treeItemAria}
    >
      <IndentWrapper $depth={depth} align="center">
        <CheckboxSlot node={node} />
        <IconAndLabel node={node} variant={variant} />
      </IndentWrapper>
    </DropdownOption>
  );
};

const HierarchicalOptionsList = ({
  nodes,
  depth,
}: {
  nodes: HierarchicalListPopoverOptionProps[];
  depth: number;
}) => (
  <>
    {nodes.map((node, index) => {
      // render the root readonly node as a branch label and not cause indentation
      if (node.parentId === null && node.isReadOnly) {
        return (
          <Fragment key={node.id}>
            <ReadOnlyBranchLabel node={node} />
            <HierarchicalOptionsList
              nodes={node.children ?? []}
              depth={depth}
            />
          </Fragment>
        );
      }
      return (
        <Fragment key={node.id}>
          <HierarchicalListPopoverOption
            node={node}
            depth={depth}
            treeItemAria={{
              'aria-level': depth + 1,
              'aria-setsize': nodes.length,
              'aria-posinset': index + 1,
            }}
          />
          <HierarchicalOptionsList
            nodes={node.children ?? []}
            depth={depth + 1}
          />
        </Fragment>
      );
    })}
  </>
);

const FlatOptionsList = ({
  nodes,
}: {
  nodes: HierarchicalListPopoverOptionProps[];
}) => (
  <Stack>
    {nodes.map(node =>
      node.isReadOnly ? null : (
        <DropdownOption
          key={node.id}
          data-tooltip-text={node.tooltip}
          aria-label={node.label}
          isDisabled={node.isDisabled}
          onClick={node.onClick}
        >
          <FlexBox align="center">
            <CheckboxSlot node={node} />
            <IconAndLabel node={node} />
          </FlexBox>
        </DropdownOption>
      )
    )}
  </Stack>
);

export const HierarchicalListPopover = (
  props: HierarchicalListPopoverProps
) => {
  const isEmpty = props.groups.every(group => group.options.length === 0);

  return (
    <DropdownContainer
      dropdownMaxHeight={props.maxHeight ?? DropdownMaxHeight.LARGE}
      dropdownMaxWidth={props.maxWidth}
    >
      <DropdownMenu>
        <FlexBox minWidth={DropdownMaxWidth.STANDARD} flexDirection="column">
          {props.filter && !props.filter?.isLoading && (
            <DropdownFilterInput
              placeholder={props.filter.placeholder ?? 'Search...'}
              value={props.filter.value}
              onValueChange={props.filter.onChange}
            />
          )}
          {props.filter?.isLoading ? (
            <FlexBox
              padding="small"
              justify="center"
              align="center"
              gap="small"
            >
              <SpinnerIcon color={colors.icon} iconSize={IconSize.SMALL} />
              {props.loadingLabel ?? 'Loading...'}
            </FlexBox>
          ) : isEmpty ? (
            <FlexBox padding="small" justify="center" align="center">
              <Text variant="text2" color="textSubtle">
                {props.noResultsLabel ?? 'No results'}
              </Text>
            </FlexBox>
          ) : (
            props.groups.map((group, i) => (
              // render the div for empty groups as well to ensure stable dom,
              // to prevent jump when selecting an option which cause selected group change
              <div key={`${i}-${group.label ?? 'group'}`}>
                {group.options.length > 0 && (
                  <>
                    {group.label && <GroupTitle>{group.label}</GroupTitle>}
                    {group.variant === 'flat' ? (
                      <FlatOptionsList nodes={group.options} />
                    ) : (
                      <Stack role="tree">
                        <HierarchicalOptionsList
                          nodes={group.options}
                          depth={0}
                        />
                      </Stack>
                    )}
                    {i < props.groups.length - 1 && <Separator />}
                  </>
                )}
              </div>
            ))
          )}
          {props.footer && (
            <>
              <Separator />
              <DropdownFootnote text={props.footer} />
            </>
          )}
        </FlexBox>
      </DropdownMenu>
    </DropdownContainer>
  );
};
